/**
 * Network Detection Manager
 *
 * Centralized network detection utility that handles different platforms
 * and provides consistent online/offline state management across the SDK.
 */

import { NetworkStateDetector } from "../types/offline.types";

export type NetworkStateCallback = (isOnline: boolean) => void;
export type NetworkCleanupFunction = () => void;

/**
 * Centralized network detection manager that consolidates all network
 * detection logic and provides a consistent interface across platforms.
 */
export class NetworkDetectionManager {
  private customDetector?: NetworkStateDetector;
  private isCurrentlyOnline: boolean = true;
  private listeners: Set<NetworkStateCallback> = new Set();
  private cleanupFunctions: NetworkCleanupFunction[] = [];

  constructor(customDetector?: NetworkStateDetector) {
    this.customDetector = customDetector;
    this.initializeDetection();
  }

  /**
   * Get current online status
   */
  get isOnline(): boolean {
    if (this.customDetector) {
      return this.customDetector.isOnline();
    }
    return this.isCurrentlyOnline;
  }

  /**
   * Add a listener for network state changes
   * @param callback - Function to call when network state changes
   * @returns Cleanup function to remove the listener
   */
  addListener(callback: NetworkStateCallback): NetworkCleanupFunction {
    this.listeners.add(callback);

    return () => {
      this.listeners.delete(callback);
    };
  }

  /**
   * Initialize network detection based on available detectors
   */
  private initializeDetection(): void {
    if (this.customDetector) {
      this.initializeCustomDetection();
    } else if (this.isWebEnvironment()) {
      this.initializeWebDetection();
    } else {
      // Default to always online for non-web environments without custom detector
      this.isCurrentlyOnline = true;
    }
  }

  /**
   * Initialize custom network detection
   */
  private initializeCustomDetection(): void {
    if (!this.customDetector) return;

    this.isCurrentlyOnline = this.customDetector.isOnline();

    // Set up listener if available
    if (this.customDetector.addListener) {
      const unsubscribe = this.customDetector.addListener(
        (isOnline: boolean) => {
          this.handleNetworkStateChange(isOnline);
        }
      );

      this.cleanupFunctions.push(unsubscribe);
    }
  }

  /**
   * Initialize web-based network detection
   */
  private initializeWebDetection(): void {
    if (typeof window === "undefined" || typeof navigator === "undefined") {
      return;
    }

    this.isCurrentlyOnline = navigator.onLine;

    const handleOnline = () => {
      this.handleNetworkStateChange(true);
    };

    const handleOffline = () => {
      this.handleNetworkStateChange(false);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Store cleanup functions
    this.cleanupFunctions.push(() => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    });
  }

  /**
   * Handle network state changes and notify listeners
   */
  private handleNetworkStateChange(isOnline: boolean): void {
    this.isCurrentlyOnline = isOnline;

    // Notify all listeners
    this.listeners.forEach((callback) => {
      try {
        callback(isOnline);
      } catch (error) {
        console.error("Error in network state callback:", error);
      }
    });
  }

  /**
   * Check if we're in a web environment
   */
  private isWebEnvironment(): boolean {
    return typeof window !== "undefined" && typeof navigator !== "undefined";
  }

  /**
   * Clean up all network detection resources
   */
  destroy(): void {
    // Clean up all event listeners and subscriptions
    this.cleanupFunctions.forEach((cleanup) => {
      try {
        cleanup();
      } catch (error) {
        console.error("Error during network detection cleanup:", error);
      }
    });

    // Clear all references
    this.cleanupFunctions = [];
    this.listeners.clear();
    this.customDetector = undefined;
  }
}
